import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import type { Tier } from './tiers';

export type CountryMeta = {
  /** ISO 3166-1 alpha-2 (e.g., "CH") */
  iso2: string;
  /** ISO 3166-1 alpha-3 (e.g., "CHE") */
  iso3: string;
  /** ISO 3166-1 numeric, zero-padded (e.g., "756"). Used to join to react-simple-maps topojson. */
  numeric: string;
  /** Canonical display name (used as the filename for the analysis, e.g., "Switzerland.tsx") */
  name: string;
  tier: Tier;
  /** One-line summary shown on hover / in compare view. Keep under ~30 words. */
  summary?: string;
  /** YYYY-MM-DD */
  lastUpdated?: string;
  /**
   * Key into ANALYSIS_REGISTRY. Equal to `name` by convention.
   * Only set when an analysis component exists in src/analyses/countries/.
   */
  analysisKey?: string;
  /** Optional explicit market viability flag from the source JSX (if present). */
  marketViability?: 'VIABLE' | 'GRAY' | 'BLOCKED';
};

/**
 * Lazy-loaded country/region analysis components.
 * Add a new entry here when converting a new JSX file.
 */
export const ANALYSIS_REGISTRY: Record<
  string,
  LazyExoticComponent<ComponentType>
> = {
  Switzerland: lazy(() => import('@/analyses/countries/Switzerland')),
  SouthAfrica: lazy(() => import('@/analyses/countries/SouthAfrica')),
  Nigeria: lazy(() => import('@/analyses/countries/Nigeria')),
  Ghana: lazy(() => import('@/analyses/countries/Ghana')),
  CoteDIvoire: lazy(() => import('@/analyses/countries/CoteDIvoire')),
  Kenya: lazy(() => import('@/analyses/countries/Kenya')),
  Gibraltar: lazy(() => import('@/analyses/countries/Gibraltar')),
  Morocco: lazy(() => import('@/analyses/countries/Morocco')),
  Qatar: lazy(() => import('@/analyses/countries/Qatar')),
  SaudiArabia: lazy(() => import('@/analyses/countries/SaudiArabia')),
  SouthKorea: lazy(() => import('@/analyses/countries/SouthKorea')),
  UAE: lazy(() => import('@/analyses/countries/UAE')),
  Cyprus: lazy(() => import('@/analyses/countries/Cyprus')),
  Monaco: lazy(() => import('@/analyses/countries/Monaco')),
  Thailand: lazy(() => import('@/analyses/countries/Thailand')),
  Latvia: lazy(() => import('@/analyses/countries/Latvia')),
  Indonesia: lazy(() => import('@/analyses/countries/Indonesia')),
  Japan: lazy(() => import('@/analyses/countries/Japan')),
  Malta: lazy(() => import('@/analyses/countries/Malta')),
  Malaysia: lazy(() => import('@/analyses/countries/Malaysia')),
  HongKong: lazy(() => import('@/analyses/countries/HongKong')),
  Lithuania: lazy(() => import('@/analyses/countries/Lithuania')),
  Singapore: lazy(() => import('@/analyses/countries/Singapore')),
  Slovenia: lazy(() => import('@/analyses/countries/Slovenia')),
  Liechtenstein: lazy(() => import('@/analyses/countries/Liechtenstein')),
  Slovakia: lazy(() => import('@/analyses/countries/Slovakia')),
  Estonia: lazy(() => import('@/analyses/countries/Estonia')),
  Croatia: lazy(() => import('@/analyses/countries/Croatia')),
  CzechRepublic: lazy(() => import('@/analyses/countries/CzechRepublic')),
  Greece: lazy(() => import('@/analyses/countries/Greece')),
  Luxembourg: lazy(() => import('@/analyses/countries/Luxembourg')),
  Norway: lazy(() => import('@/analyses/countries/Norway')),
  Sweden: lazy(() => import('@/analyses/countries/Sweden')),
  Finland: lazy(() => import('@/analyses/countries/Finland')),
  Ireland: lazy(() => import('@/analyses/countries/Ireland')),
  Denmark: lazy(() => import('@/analyses/countries/Denmark')),
  India: lazy(() => import('@/analyses/countries/India')),
  Nicaragua: lazy(() => import('@/analyses/countries/Nicaragua')),
  Honduras: lazy(() => import('@/analyses/countries/Honduras')),
  NewZealand: lazy(() => import('@/analyses/countries/NewZealand')),
  Belgium: lazy(() => import('@/analyses/countries/Belgium')),
  France: lazy(() => import('@/analyses/countries/France')),
  Poland: lazy(() => import('@/analyses/countries/Poland')),
  Australia: lazy(() => import('@/analyses/countries/Australia')),
  UnitedKingdom: lazy(() => import('@/analyses/countries/UnitedKingdom')),
  Bulgaria: lazy(() => import('@/analyses/countries/Bulgaria')),
  Turkey: lazy(() => import('@/analyses/countries/Turkey')),
  Canada: lazy(() => import('@/analyses/countries/Canada')),
  Iceland: lazy(() => import('@/analyses/countries/Iceland')),
  Israel: lazy(() => import('@/analyses/countries/Israel')),
  Netherlands: lazy(() => import('@/analyses/countries/Netherlands')),
  Germany: lazy(() => import('@/analyses/countries/Germany')),
  Portugal: lazy(() => import('@/analyses/countries/Portugal')),
  Russia: lazy(() => import('@/analyses/countries/Russia')),
  Austria: lazy(() => import('@/analyses/countries/Austria')),
  Ecuador: lazy(() => import('@/analyses/countries/Ecuador')),
  UnitedStates: lazy(() => import('@/analyses/countries/UnitedStates')),
  Spain: lazy(() => import('@/analyses/countries/Spain')),
  Uruguay: lazy(() => import('@/analyses/countries/Uruguay')),
  Venezuela: lazy(() => import('@/analyses/countries/Venezuela')),
  Bolivia: lazy(() => import('@/analyses/countries/Bolivia')),
  Italy: lazy(() => import('@/analyses/countries/Italy')),
  DominicanRepublic: lazy(() => import('@/analyses/countries/DominicanRepublic')),
  Mexico: lazy(() => import('@/analyses/countries/Mexico')),
  Paraguay: lazy(() => import('@/analyses/countries/Paraguay')),
  Guatemala: lazy(() => import('@/analyses/countries/Guatemala')),
  ElSalvador: lazy(() => import('@/analyses/countries/ElSalvador')),
  Argentina: lazy(() => import('@/analyses/countries/Argentina')),
  Hungary: lazy(() => import('@/analyses/countries/Hungary')),
  Colombia: lazy(() => import('@/analyses/countries/Colombia')),
  Panama: lazy(() => import('@/analyses/countries/Panama')),
  Brazil: lazy(() => import('@/analyses/countries/Brazil')),
  Peru: lazy(() => import('@/analyses/countries/Peru')),
  CostaRica: lazy(() => import('@/analyses/countries/CostaRica')),
  Chile: lazy(() => import('@/analyses/countries/Chile')),
  PuertoRico: lazy(() => import('@/analyses/countries/PuertoRico')),
  Romania: lazy(() => import('@/analyses/countries/Romania')),
  // ── Regional analyses ────────────────────────────────────────────
  EuropeanUnion: lazy(() => import('@/analyses/regions/EuropeanUnion')),
};

/**
 * Tier cheatsheet:
 *   easy       — plug-and-play (clean EU, cross-border + OSS works)
 *   moderate   — Medium: local compliance work, standard ops model works
 *   restricted — Hard: real restrictions, narrow configurations only
 *   blocked    — Not viable today (OFAC, structural bans, no PSP)
 */
export const COUNTRIES: CountryMeta[] = [
  // ── Europe ─────────────────────────────────────────────────────────
  { iso2: 'AL', iso3: 'ALB', numeric: '008', name: 'Albania', tier: 'not-analyzed' },
  { iso2: 'AD', iso3: 'AND', numeric: '020', name: 'Andorra', tier: 'not-analyzed' },
  {
    iso2: 'AT', iso3: 'AUT', numeric: '040', name: 'Austria',
    tier: 'easy',
    summary: 'No statutory resale ban. Irish EU node via OSS is clean — 20% VAT on fees, no withholding, withdrawal exempt. OGH 2025 confirms fees survive cancellation.',
    lastUpdated: '2026-04-16', analysisKey: 'Austria', marketViability: 'VIABLE',
  },
  { iso2: 'BY', iso3: 'BLR', numeric: '112', name: 'Belarus', tier: 'not-analyzed' },
  {
    iso2: 'BE', iso3: 'BEL', numeric: '056', name: 'Belgium',
    tier: 'restricted',
    summary: 'Law of 30 July 2013 bans habitual resale with explicit platform liability. Face-value only; CJEU TICOMBO referral pending could invalidate.',
    lastUpdated: '2026-04-16', analysisKey: 'Belgium', marketViability: 'GRAY',
  },
  { iso2: 'BA', iso3: 'BIH', numeric: '070', name: 'Bosnia and Herzegovina', tier: 'not-analyzed' },
  {
    iso2: 'BG', iso3: 'BGR', numeric: '100', name: 'Bulgaria',
    tier: 'easy',
    summary: 'Green-light EU market. No resale restrictions, no price caps. Irish node via OSS (20% VAT on fees), Stripe Connect, no withholding.',
    lastUpdated: '2026-04-16', analysisKey: 'Bulgaria', marketViability: 'VIABLE',
  },
  {
    iso2: 'HR', iso3: 'HRV', numeric: '191', name: 'Croatia',
    tier: 'easy',
    summary: 'Clean EU market. No anti-scalping, no withholding, no fiskalizacija for non-established. EU node via OSS (25% VAT on fees) + Stripe Connect.',
    lastUpdated: '2026-04-16', analysisKey: 'Croatia', marketViability: 'VIABLE',
  },
  {
    iso2: 'CY', iso3: 'CYP', numeric: '196', name: 'Cyprus',
    tier: 'easy',
    summary: 'One of the cleanest EU markets. No anti-scalping, no price caps, no WHT, no PE risk. Cross-border from Spain SL or Irish Ltd via Stripe Connect.',
    lastUpdated: '2026-04-15', analysisKey: 'Cyprus', marketViability: 'VIABLE',
  },
  {
    iso2: 'CZ', iso3: 'CZE', numeric: '203', name: 'Czech Republic',
    tier: 'easy',
    summary: 'One of the most permissive EU markets. No anti-scalping, no bot bans. Irish EU node via Stripe Connect. DPH 21% on fees via OSS. Withdrawal excluded.',
    lastUpdated: '2026-04-16', analysisKey: 'CzechRepublic', marketViability: 'VIABLE',
  },
  {
    iso2: 'DK', iso3: 'DNK', numeric: '208', name: 'Denmark',
    tier: 'easy',
    summary: 'Resale allowed with organizer consent (Lov nr. 458/2007 §1). Menta model affirmatively permitted by statute. Ireland EU node via OSS (25% VAT on fees).',
    lastUpdated: '2026-04-16', analysisKey: 'Denmark', marketViability: 'VIABLE',
  },
  {
    iso2: 'EE', iso3: 'EST', numeric: '233', name: 'Estonia',
    tier: 'easy',
    summary: 'Clean EU market. Ireland Ltd cross-border via Stripe Connect. No anti-scalping, no withholding. 24% VAT on fees via OSS.',
    lastUpdated: '2026-04-16', analysisKey: 'Estonia', marketViability: 'VIABLE',
  },
  {
    iso2: 'FI', iso3: 'FIN', numeric: '246', name: 'Finland',
    tier: 'easy',
    summary: 'Highly favorable. Fully legal, no price caps, no anti-scalping. Ireland Ltd cross-border via Stripe Connect + OSS. 25.5% VAT on fees. Withdrawal exempt.',
    lastUpdated: '2026-04-16', analysisKey: 'Finland', marketViability: 'VIABLE',
  },
  {
    iso2: 'FR', iso3: 'FRA', numeric: '250', name: 'France',
    tier: 'easy',
    summary: 'Art. 313-6-2 criminalizes only habitual + unauthorized resale — organizer consent eliminates liability. Ireland node via OSS, TVA 20% on fees.',
    lastUpdated: '2026-04-16', analysisKey: 'France', marketViability: 'VIABLE',
  },
  {
    iso2: 'DE', iso3: 'DEU', numeric: '276', name: 'Germany',
    tier: 'moderate',
    summary: 'Legal today (BGH 2008 precedent). Ireland node + Stripe Connect, 19% VAT on fees via OSS. 2025 coalition agreement commits to statutory price caps — bill expected within 12-18 months.',
    lastUpdated: '2026-04-16', analysisKey: 'Germany', marketViability: 'VIABLE',
  },
  {
    iso2: 'GI', iso3: 'GIB', numeric: '292', name: 'Gibraltar',
    tier: 'easy',
    summary: 'Simplest jurisdiction analyzed. Zero VAT, zero WHT, zero DST, no anti-scalping. Stripe Connect via UK passporting. Serve passively from UK node.',
    lastUpdated: '2026-04-15', analysisKey: 'Gibraltar', marketViability: 'VIABLE',
  },
  {
    iso2: 'GR', iso3: 'GRC', numeric: '300', name: 'Greece',
    tier: 'moderate',
    summary: 'Cultural events clean (OSS, no Greek entity). Sports DE FACTO BLOCKED by mandatory Gov.gr Wallet personalization (Law 2725/1999) — exclude sports at launch.',
    lastUpdated: '2026-04-16', analysisKey: 'Greece', marketViability: 'VIABLE',
  },
  { iso2: 'HU', iso3: 'HUN', numeric: '348', name: 'Hungary',
    tier: 'easy',
    summary: 'Cross-border EU model via local ticketing partner (Cooltix Kft). 27% VAT on fees under Art. 45 (Hungary as supplier). Above-face resale formally prohibited (criminal up to 2yr) but unenforced — face-value model compliant.',
    lastUpdated: '2026-04-16', analysisKey: 'Hungary', marketViability: 'VIABLE',
  },
  {
    iso2: 'IS', iso3: 'ISL', numeric: '352', name: 'Iceland',
    tier: 'easy',
    summary: 'No resale restrictions. Event admission VSK-exempt; only fees at 24%. Spain node via EEA freedom of services. Material: Stripe cannot onboard Icelandic sellers — use Wise.',
    lastUpdated: '2026-04-16', analysisKey: 'Iceland', marketViability: 'VIABLE',
  },
  {
    iso2: 'IE', iso3: 'IRL', numeric: '372', name: 'Ireland',
    tier: 'moderate',
    summary: 'Sale of Tickets Act 2021 criminalizes above-face-value resale at designated events (≥1K capacity). Face-value Menta model fully compliant. 12.5% CIT + 23% VAT.',
    lastUpdated: '2026-04-16', analysisKey: 'Ireland', marketViability: 'VIABLE',
  },
  {
    iso2: 'IT', iso3: 'ITA', numeric: '380', name: 'Italy',
    tier: 'moderate',
    summary: 'Strictest EU secondary regime (Battelli Law, AGCOM fines €5K-180K/event). Face-value via SIAE-certified partner. Menta EU node escapes IVA (Art. 45 → IE/ES VAT) + no SDI.',
    lastUpdated: '2026-04-16', analysisKey: 'Italy', marketViability: 'VIABLE',
  },
  { iso2: 'XK', iso3: 'XKS', numeric: '983', name: 'Kosovo', tier: 'not-analyzed' },
  {
    iso2: 'LV', iso3: 'LVA', numeric: '428', name: 'Latvia',
    tier: 'easy',
    summary: 'One of the cleanest EU markets. No anti-scalping, no price caps. Spain SL or Irish Ltd via OSS — 21% VAT on fees only. No WHT. Withdrawal exempt.',
    lastUpdated: '2026-04-15', analysisKey: 'Latvia', marketViability: 'VIABLE',
  },
  {
    iso2: 'LI', iso3: 'LIE', numeric: '438', name: 'Liechtenstein',
    tier: 'easy',
    summary: 'One of Europe\'s most permissive jurisdictions. No anti-scalping, no price caps. EEA + Swiss customs union. Ireland node preferred. MWST 8.1% on fees.',
    lastUpdated: '2026-04-16', analysisKey: 'Liechtenstein', marketViability: 'VIABLE',
  },
  {
    iso2: 'LT', iso3: 'LTU', numeric: '440', name: 'Lithuania',
    tier: 'easy',
    summary: 'Low-complexity EU market. No anti-scalping, no price caps. EU node via Stripe Connect + OSS. Lithuanian-language content mandatory from Jan 2026.',
    lastUpdated: '2026-04-16', analysisKey: 'Lithuania', marketViability: 'VIABLE',
  },
  {
    iso2: 'LU', iso3: 'LUX', numeric: '442', name: 'Luxembourg',
    tier: 'easy',
    summary: 'One of the most permissive EU jurisdictions. Consumer Minister confirmed resale above face value is not unfair. 17% VAT on fees via OSS. No withholding.',
    lastUpdated: '2026-04-16', analysisKey: 'Luxembourg', marketViability: 'VIABLE',
  },
  {
    iso2: 'MT', iso3: 'MLT', numeric: '470', name: 'Malta',
    tier: 'easy',
    summary: 'Clean, low-friction EU market. Spain SL or Irish Ltd cross-border — no Malta registration, no local VAT. 18% VAT on fees via OSS. No withholding.',
    lastUpdated: '2026-04-15', analysisKey: 'Malta', marketViability: 'VIABLE',
  },
  { iso2: 'MD', iso3: 'MDA', numeric: '498', name: 'Moldova', tier: 'not-analyzed' },
  {
    iso2: 'MC', iso3: 'MCO', numeric: '492', name: 'Monaco',
    tier: 'easy',
    summary: 'Clean market. No personal income tax, no withholding, no anti-scalping. Irish Ltd cross-border via OSS — French TVA 20% on fees (Monaco = France for VAT).',
    lastUpdated: '2026-04-15', analysisKey: 'Monaco', marketViability: 'VIABLE',
  },
  { iso2: 'ME', iso3: 'MNE', numeric: '499', name: 'Montenegro', tier: 'not-analyzed' },
  {
    iso2: 'NL', iso3: 'NLD', numeric: '528', name: 'Netherlands',
    tier: 'easy',
    summary: 'Clean, low-friction EU market. ACM permissive on secondary ticketing. Spain SL preferred (Ireland has Sale of Tickets Act risk). 21% VAT on fees via OSS.',
    lastUpdated: '2026-04-16', analysisKey: 'Netherlands', marketViability: 'VIABLE',
  },
  { iso2: 'MK', iso3: 'MKD', numeric: '807', name: 'North Macedonia', tier: 'not-analyzed' },
  {
    iso2: 'NO', iso3: 'NOR', numeric: '578', name: 'Norway',
    tier: 'moderate',
    summary: 'svartebørsloven (2007) caps resale at face value for all culture/sports events. Ireland Ltd via EEA. 25% MVA on fees. DPI reporting from Jan 2026. Revenue = service fees only.',
    lastUpdated: '2026-04-16', analysisKey: 'Norway', marketViability: 'VIABLE',
  },
  {
    iso2: 'PL', iso3: 'POL', numeric: '616', name: 'Poland',
    tier: 'restricted',
    summary: 'Art. 133 Code of Petty Offences criminalizes ANY for-profit ticket resale. No de minimis. Marketplace must cap at face value only (fan-to-fan exchange model).',
    lastUpdated: '2026-04-16', analysisKey: 'Poland', marketViability: 'GRAY',
  },
  {
    iso2: 'PT', iso3: 'PRT', numeric: '620', name: 'Portugal',
    tier: 'moderate',
    summary: 'Decreto-Lei 28/84 Art. 35 criminalizes above-face-value resale (6mo-3yr). Menta face-value model compliant via Spain node + OSS (which exempts PT\'s SAF-T regime).',
    lastUpdated: '2026-04-16', analysisKey: 'Portugal', marketViability: 'VIABLE',
  },
  {
    iso2: 'RO', iso3: 'ROU', numeric: '642', name: 'Romania',
    tier: 'easy',
    summary: 'Clean EU market. No anti-scalping, no price caps, no e-Factura for foreign EU. Ireland node via Art. 45 — 23% Irish VAT on fees only. Foreign-payer rule eliminates WHT.',
    lastUpdated: '2026-04-16', analysisKey: 'Romania', marketViability: 'VIABLE',
  },
  {
    iso2: 'RU', iso3: 'RUS', numeric: '643', name: 'Russia',
    tier: 'blocked',
    summary: 'MARKET BLOCKED. OFAC IT Services Determination (Sept 2024) prohibits US SaaS to Russia. Federal Law 193-FZ bans above-face-value resale. No Stripe/intl PSPs.',
    lastUpdated: '2026-04-16', analysisKey: 'Russia', marketViability: 'BLOCKED',
  },
  { iso2: 'SM', iso3: 'SMR', numeric: '674', name: 'San Marino', tier: 'not-analyzed' },
  { iso2: 'RS', iso3: 'SRB', numeric: '688', name: 'Serbia', tier: 'not-analyzed' },
  {
    iso2: 'SK', iso3: 'SVK', numeric: '703', name: 'Slovakia',
    tier: 'easy',
    summary: 'Low-barrier EU market. No anti-scalping, no price caps. Irish EU node via Stripe Connect — no local establishment or Slovak VAT registration needed.',
    lastUpdated: '2026-04-16', analysisKey: 'Slovakia', marketViability: 'VIABLE',
  },
  {
    iso2: 'SI', iso3: 'SVN', numeric: '705', name: 'Slovenia',
    tier: 'easy',
    summary: 'Favorable EU market. No anti-scalping, no price caps, no ticket resale statute. EU node cross-border — DDV 22% on fees via OSS. Slovenian-language UX mandatory.',
    lastUpdated: '2026-04-16', analysisKey: 'Slovenia', marketViability: 'VIABLE',
  },
  {
    iso2: 'ES', iso3: 'ESP', numeric: '724', name: 'Spain',
    tier: 'easy',
    summary: 'Viable market, clean tax architecture. IVA 21% on fees only; casual sellers outside IVA. Autonomous community patchwork managed via product-level price caps.',
    lastUpdated: '2026-04-16', analysisKey: 'Spain', marketViability: 'VIABLE',
  },
  {
    iso2: 'SE', iso3: 'SWE', numeric: '752', name: 'Sweden',
    tier: 'moderate',
    summary: 'Fully legal, no price caps. Ireland cross-border via Stripe Connect + OSS. VAT 25% on fees. CRITICAL: 14-day Ångerrätt applies to resale — full refund including fees required.',
    lastUpdated: '2026-04-16', analysisKey: 'Sweden', marketViability: 'VIABLE',
  },
  {
    iso2: 'CH', iso3: 'CHE', numeric: '756', name: 'Switzerland',
    tier: 'easy',
    summary: 'Most permissive jurisdiction for authorized resale. Federal Supreme Court validated resale platforms. No price caps, no anti-scalping. Ireland node optimal.',
    lastUpdated: '2026-04-16', analysisKey: 'Switzerland', marketViability: 'VIABLE',
  },
  {
    iso2: 'TR', iso3: 'TUR', numeric: '792', name: 'Turkey',
    tier: 'moderate',
    summary: 'Local Turkish co required. Stripe unavailable — use dLocal/Lidio. KDV 20% on fees. Law 6222 criminalizes sports resale — non-sports entertainment clean.',
    lastUpdated: '2026-04-16', analysisKey: 'Turkey', marketViability: 'VIABLE',
  },
  { iso2: 'UA', iso3: 'UKR', numeric: '804', name: 'Ukraine', tier: 'not-analyzed' },
  {
    iso2: 'GB', iso3: 'GBR', numeric: '826', name: 'United Kingdom',
    tier: 'moderate',
    summary: 'UK Ltd + Stripe Connect. VAT 20% on fees, no WHT. CRA 2015 disclosure + DMCCA 2024 all-in pricing. Face-value price cap announced Nov 2025, bill expected 2026.',
    lastUpdated: '2026-04-16', analysisKey: 'UnitedKingdom', marketViability: 'VIABLE',
  },
  { iso2: 'VA', iso3: 'VAT', numeric: '336', name: 'Vatican City', tier: 'not-analyzed' },

  // ── Americas ───────────────────────────────────────────────────────
  { iso2: 'AG', iso3: 'ATG', numeric: '028', name: 'Antigua and Barbuda', tier: 'not-analyzed' },
  {
    iso2: 'AR', iso3: 'ARG', numeric: '032', name: 'Argentina',
    tier: 'moderate',
    summary: 'Menta AR direct (local entity). IVA 21% on fees only. No WHT on casual sellers. Risks: BCRA PSP registration, CABA for-profit contravention, mandatory retracto button.',
    lastUpdated: '2026-04-16', analysisKey: 'Argentina', marketViability: 'VIABLE',
  },
  { iso2: 'BS', iso3: 'BHS', numeric: '044', name: 'Bahamas', tier: 'not-analyzed' },
  { iso2: 'BB', iso3: 'BRB', numeric: '052', name: 'Barbados', tier: 'not-analyzed' },
  { iso2: 'BZ', iso3: 'BLZ', numeric: '084', name: 'Belize', tier: 'not-analyzed' },
  {
    iso2: 'BO', iso3: 'BOL', numeric: '068', name: 'Bolivia',
    tier: 'moderate',
    summary: 'Viable via local ticketera. IVA 13% + IT 3% on fees; 12.5% IUE-BE on Menta fees (no treaty). Stripe N/A — dLocal. FX crisis is main constraint (bank commissions >60%).',
    lastUpdated: '2026-04-16', analysisKey: 'Bolivia', marketViability: 'VIABLE',
  },
  {
    iso2: 'BR', iso3: 'BRA', numeric: '076', name: 'Brazil',
    tier: 'moderate',
    summary: 'Viable via local LTDA in São Paulo. ~13-16% aggregate on fees. Sports anti-scalping criminal (Lei 14.597/2023). PL 3115 "Lei Taylor Swift" pending — would criminalize all above-face.',
    lastUpdated: '2026-04-16', analysisKey: 'Brazil', marketViability: 'VIABLE',
  },
  {
    iso2: 'CA', iso3: 'CAN', numeric: '124', name: 'Canada',
    tier: 'moderate',
    summary: 'Menta US cross-border. GST/HST 5-15% on fees via simplified. Ontario Bill 94 pending face-value cap (expected before FIFA WC 2026). ACCC drip-pricing risk.',
    lastUpdated: '2026-04-16', analysisKey: 'Canada', marketViability: 'VIABLE',
  },
  {
    iso2: 'CL', iso3: 'CHL', numeric: '152', name: 'Chile',
    tier: 'moderate',
    summary: 'Local SpA required. 19% IVA on fees; tickets outside (incorporeal right). Chile-US treaty (2024) eliminates cross-border WHT — 0% business profits. PSP must hold funds (Art. 39 LGB).',
    lastUpdated: '2026-04-16', analysisKey: 'Chile', marketViability: 'VIABLE',
  },
  {
    iso2: 'CO', iso3: 'COL', numeric: '170', name: 'Colombia',
    tier: 'moderate',
    summary: 'Local SAS + dLocal. Ticket IVA-excluded (DIAN concepto); fees 19% IVA. Retracto 5-day non-waivable refunds ALL amounts — budget 3-5%. 20% cross-border WHT (no treaty).',
    lastUpdated: '2026-04-16', analysisKey: 'Colombia', marketViability: 'VIABLE',
  },
  {
    iso2: 'CR', iso3: 'CRI', numeric: '188', name: 'Costa Rica',
    tier: 'moderate',
    summary: 'Ticketera invoices locally; dLocal US holds funds. Casual sellers outside IVA (no habitualidad). 13% IVA on fees. 8-day withdrawal right is main gray area.',
    lastUpdated: '2026-04-15', analysisKey: 'CostaRica', marketViability: 'VIABLE',
  },
  { iso2: 'CU', iso3: 'CUB', numeric: '192', name: 'Cuba', tier: 'not-analyzed' },
  { iso2: 'DM', iso3: 'DMA', numeric: '212', name: 'Dominica', tier: 'not-analyzed' },
  {
    iso2: 'DO', iso3: 'DOM', numeric: '214', name: 'Dominican Republic',
    tier: 'moderate',
    summary: 'Local UEPA ticketing + dLocal. Ticket ITBIS-exempt (Art. 344); fees 18%. CRITICAL: 27% gross WHT to Menta (no treaty). Pro Consumidor precedent on resale pricing.',
    lastUpdated: '2026-04-16', analysisKey: 'DominicanRepublic', marketViability: 'VIABLE',
  },
  {
    iso2: 'EC', iso3: 'ECU', numeric: '218', name: 'Ecuador',
    tier: 'moderate',
    summary: '10% listing price cap (not fees — model works). IVA 15% on fees; IR+IVA WHT 2-10%/30-100% on payouts. Stripe N/A — Kushki/dLocal. ~30% cross-border tax (no treaty).',
    lastUpdated: '2026-04-16', analysisKey: 'Ecuador', marketViability: 'VIABLE',
  },
  {
    iso2: 'SV', iso3: 'SLV', numeric: '222', name: 'El Salvador',
    tier: 'moderate',
    summary: 'USD dollarized. 13% IVA on fees. 10% ISR WHT on payouts. DTE e-invoicing via REST API. 8-day withdrawal right with NO event exception — HIGH risk.',
    lastUpdated: '2026-04-16', analysisKey: 'ElSalvador', marketViability: 'VIABLE',
  },
  { iso2: 'GD', iso3: 'GRD', numeric: '308', name: 'Grenada', tier: 'not-analyzed' },
  {
    iso2: 'GT', iso3: 'GTM', numeric: '320', name: 'Guatemala',
    tier: 'moderate',
    summary: 'Local entity + FEL mandatory. 12% IVA on fees. ~15% factura especial withholding on every casual seller payout (12% IVA + 5% ISR, unavoidable). No anti-scalping law.',
    lastUpdated: '2026-04-16', analysisKey: 'Guatemala', marketViability: 'VIABLE',
  },
  { iso2: 'GY', iso3: 'GUY', numeric: '328', name: 'Guyana', tier: 'not-analyzed' },
  { iso2: 'HT', iso3: 'HTI', numeric: '332', name: 'Haiti', tier: 'not-analyzed' },
  {
    iso2: 'HN', iso3: 'HND', numeric: '340', name: 'Honduras',
    tier: 'restricted',
    summary: 'Permissive resale law, but heavy ops burden: local co required, Stripe N/A (BAC/Ficohsa/dLocal), 25% WHT to Menta (no treaty), FX controls, CAI invoicing.',
    lastUpdated: '2026-04-16', analysisKey: 'Honduras', marketViability: 'VIABLE',
  },
  { iso2: 'JM', iso3: 'JAM', numeric: '388', name: 'Jamaica', tier: 'not-analyzed' },
  {
    iso2: 'MX', iso3: 'MEX', numeric: '484', name: 'Mexico',
    tier: 'moderate',
    summary: 'Local S. de R.L. de C.V. + Stripe Connect. 16% IVA + 2.5% ISR WHT. Plataformas Tecnológicas regime with kill-switch for non-compliance. 10% royalty WHT to US (treaty).',
    lastUpdated: '2026-04-16', analysisKey: 'Mexico', marketViability: 'VIABLE',
  },
  {
    iso2: 'NI', iso3: 'NIC', numeric: '558', name: 'Nicaragua',
    tier: 'restricted',
    summary: 'No anti-scalping, but high friction: Stripe N/A (BAC/LAFISE), 20% definitive WHT to Menta (no treaty), BCN Ley 1232 PSP licensing. US sanctions exposure (OFAC, NICA Act).',
    lastUpdated: '2026-04-16', analysisKey: 'Nicaragua', marketViability: 'VIABLE',
  },
  {
    iso2: 'PA', iso3: 'PAN', numeric: '591', name: 'Panama',
    tier: 'moderate',
    summary: 'Low-friction LATAM. USD dollarized, no anti-scalping, no withdrawal rights. Local S.A. + dLocal; 7% ITBMS on fees. 12.5% + 7% WHT on Menta fees (no treaty).',
    lastUpdated: '2026-04-16', analysisKey: 'Panama', marketViability: 'VIABLE',
  },
  {
    iso2: 'PY', iso3: 'PRY', numeric: '600', name: 'Paraguay',
    tier: 'moderate',
    summary: 'Highly favorable resale legality. Local operator + dLocal (Stripe N/A). IVA 10% on fees only. Autofactura ~8% IRP withholding on casual payouts. Law 7503/2025 payments risk.',
    lastUpdated: '2026-04-16', analysisKey: 'Paraguay', marketViability: 'VIABLE',
  },
  {
    iso2: 'PE', iso3: 'PER', numeric: '604', name: 'Peru',
    tier: 'moderate',
    summary: 'Menta Peru SAC + Niubiz/Culqi (Stripe N/A). IGV 18% on fees; casual sellers zero IGV/income tax. No withholding on payouts. INDECOPI aggressive on ticketing transparency.',
    lastUpdated: '2026-04-16', analysisKey: 'Peru', marketViability: 'VIABLE',
  },
  {
    iso2: 'PR', iso3: 'PRI', numeric: '630', name: 'Puerto Rico',
    tier: 'moderate',
    summary: 'Delaware C-corp registers via SURI remotely — no local entity. IVU 11.5% as marketplace facilitator. Form 480.6G annual reporting for all sellers (incl. foreign). MT counsel opinion needed.',
    lastUpdated: '2026-04-16', analysisKey: 'PuertoRico', marketViability: 'VIABLE',
  },
  { iso2: 'KN', iso3: 'KNA', numeric: '659', name: 'Saint Kitts and Nevis', tier: 'not-analyzed' },
  { iso2: 'LC', iso3: 'LCA', numeric: '662', name: 'Saint Lucia', tier: 'not-analyzed' },
  { iso2: 'VC', iso3: 'VCT', numeric: '670', name: 'Saint Vincent and the Grenadines', tier: 'not-analyzed' },
  { iso2: 'SR', iso3: 'SUR', numeric: '740', name: 'Suriname', tier: 'not-analyzed' },
  { iso2: 'TT', iso3: 'TTO', numeric: '780', name: 'Trinidad and Tobago', tier: 'not-analyzed' },
  {
    iso2: 'US', iso3: 'USA', numeric: '840', name: 'United States',
    tier: 'moderate',
    summary: 'Menta Tickets FL operates nationwide via Stripe Connect. Marketplace facilitator in 46 jurisdictions. FTC Junk Fees Rule active ($51K/violation). 6 price-cap states. 1099-K federal $20K/200.',
    lastUpdated: '2026-04-16', analysisKey: 'UnitedStates', marketViability: 'VIABLE',
  },
  {
    iso2: 'UY', iso3: 'URY', numeric: '858', name: 'Uruguay',
    tier: 'moderate',
    summary: 'Local ticketera (Stripe N/A). BCU PSPC registration. IVA 22% on fees via CFE VCA regime. No withholding. CRITICAL: 5-day withdrawal right with no event exception.',
    lastUpdated: '2026-04-16', analysisKey: 'Uruguay', marketViability: 'VIABLE',
  },
  {
    iso2: 'VE', iso3: 'VEN', numeric: '862', name: 'Venezuela',
    tier: 'blocked',
    summary: 'MARKET BLOCKED. Three near-fatal barriers: OFAC EO 13884 + municipal resale bans (LOPJ 8-10yr prison) + no international PSPs. Proceed only with OFAC counsel + cleared partner.',
    lastUpdated: '2026-04-16', analysisKey: 'Venezuela', marketViability: 'GRAY',
  },

  // ── Africa ────────────────────────────────────────────────────────
  { iso2: 'DZ', iso3: 'DZA', numeric: '012', name: 'Algeria', tier: 'not-analyzed' },
  { iso2: 'AO', iso3: 'AGO', numeric: '024', name: 'Angola', tier: 'not-analyzed' },
  { iso2: 'BJ', iso3: 'BEN', numeric: '204', name: 'Benin', tier: 'not-analyzed' },
  { iso2: 'BW', iso3: 'BWA', numeric: '072', name: 'Botswana', tier: 'not-analyzed' },
  { iso2: 'BF', iso3: 'BFA', numeric: '854', name: 'Burkina Faso', tier: 'not-analyzed' },
  { iso2: 'BI', iso3: 'BDI', numeric: '108', name: 'Burundi', tier: 'not-analyzed' },
  { iso2: 'CV', iso3: 'CPV', numeric: '132', name: 'Cabo Verde', tier: 'not-analyzed' },
  { iso2: 'CM', iso3: 'CMR', numeric: '120', name: 'Cameroon', tier: 'not-analyzed' },
  { iso2: 'CF', iso3: 'CAF', numeric: '140', name: 'Central African Republic', tier: 'not-analyzed' },
  { iso2: 'TD', iso3: 'TCD', numeric: '148', name: 'Chad', tier: 'not-analyzed' },
  { iso2: 'KM', iso3: 'COM', numeric: '174', name: 'Comoros', tier: 'not-analyzed' },
  { iso2: 'CG', iso3: 'COG', numeric: '178', name: 'Congo', tier: 'not-analyzed' },
  { iso2: 'CD', iso3: 'COD', numeric: '180', name: 'Democratic Republic of the Congo', tier: 'not-analyzed' },
  {
    iso2: 'CI', iso3: 'CIV', numeric: '384', name: "Côte d'Ivoire",
    tier: 'restricted',
    summary: 'Legal with no price caps, but 10-day cooling-off right with NO event exception could break economics. Local entity + BCEAO PSP + FNE e-invoicing + 38% combined WHT+TVA.',
    lastUpdated: '2026-04-15', analysisKey: 'CoteDIvoire', marketViability: 'VIABLE',
  },
  { iso2: 'DJ', iso3: 'DJI', numeric: '262', name: 'Djibouti', tier: 'not-analyzed' },
  { iso2: 'EG', iso3: 'EGY', numeric: '818', name: 'Egypt', tier: 'not-analyzed' },
  { iso2: 'GQ', iso3: 'GNQ', numeric: '226', name: 'Equatorial Guinea', tier: 'not-analyzed' },
  { iso2: 'ER', iso3: 'ERI', numeric: '232', name: 'Eritrea', tier: 'not-analyzed' },
  { iso2: 'SZ', iso3: 'SWZ', numeric: '748', name: 'Eswatini', tier: 'not-analyzed' },
  { iso2: 'ET', iso3: 'ETH', numeric: '231', name: 'Ethiopia', tier: 'not-analyzed' },
  { iso2: 'GA', iso3: 'GAB', numeric: '266', name: 'Gabon', tier: 'not-analyzed' },
  { iso2: 'GM', iso3: 'GMB', numeric: '270', name: 'Gambia', tier: 'not-analyzed' },
  {
    iso2: 'GH', iso3: 'GHA', numeric: '288', name: 'Ghana',
    tier: 'moderate',
    summary: 'Resale fully legal. Requires local entity + Paystack (Stripe N/A), E-VAT real-time e-invoicing, BoG licensing. 20% combined VAT on fees, 20% non-resident WHT (no US treaty).',
    lastUpdated: '2026-04-15', analysisKey: 'Ghana', marketViability: 'VIABLE',
  },
  { iso2: 'GN', iso3: 'GIN', numeric: '324', name: 'Guinea', tier: 'not-analyzed' },
  { iso2: 'GW', iso3: 'GNB', numeric: '624', name: 'Guinea-Bissau', tier: 'not-analyzed' },
  {
    iso2: 'KE', iso3: 'KEN', numeric: '404', name: 'Kenya',
    tier: 'moderate',
    summary: 'Resale legal. Requires local entity + Paystack/dLocal (Stripe N/A), eTIMS e-invoicing. 16% VAT + 3% SEPT; 20% WHT to Menta US (no treaty). FATF grey list.',
    lastUpdated: '2026-04-15', analysisKey: 'Kenya', marketViability: 'VIABLE',
  },
  { iso2: 'LS', iso3: 'LSO', numeric: '426', name: 'Lesotho', tier: 'not-analyzed' },
  { iso2: 'LR', iso3: 'LBR', numeric: '430', name: 'Liberia', tier: 'not-analyzed' },
  { iso2: 'LY', iso3: 'LBY', numeric: '434', name: 'Libya', tier: 'not-analyzed' },
  { iso2: 'MG', iso3: 'MDG', numeric: '450', name: 'Madagascar', tier: 'not-analyzed' },
  { iso2: 'MW', iso3: 'MWI', numeric: '454', name: 'Malawi', tier: 'not-analyzed' },
  { iso2: 'ML', iso3: 'MLI', numeric: '466', name: 'Mali', tier: 'not-analyzed' },
  { iso2: 'MR', iso3: 'MRT', numeric: '478', name: 'Mauritania', tier: 'not-analyzed' },
  { iso2: 'MU', iso3: 'MUS', numeric: '480', name: 'Mauritius', tier: 'not-analyzed' },
  {
    iso2: 'MA', iso3: 'MAR', numeric: '504', name: 'Morocco',
    tier: 'restricted',
    summary: 'Above-face-value resale prosecuted as fraud (Penal Code Art. 540, 1-5yr prison). 2022 WC + 2025 AFCON arrests. Face-value + organizer auth is only safe model. US treaty protects from WHT.',
    lastUpdated: '2026-04-15', analysisKey: 'Morocco', marketViability: 'GRAY',
  },
  { iso2: 'MZ', iso3: 'MOZ', numeric: '508', name: 'Mozambique', tier: 'not-analyzed' },
  { iso2: 'NA', iso3: 'NAM', numeric: '516', name: 'Namibia', tier: 'not-analyzed' },
  { iso2: 'NE', iso3: 'NER', numeric: '562', name: 'Niger', tier: 'not-analyzed' },
  {
    iso2: 'NG', iso3: 'NGA', numeric: '566', name: 'Nigeria',
    tier: 'moderate',
    summary: 'Resale fully legal. Local Nigerian entity + Paystack (not Stripe), bank-trust custody, 7.5% VAT, 10% WHT on tech fees, FIRS e-invoicing. Cross-border from US blocked by FX controls.',
    lastUpdated: '2026-04-15', analysisKey: 'Nigeria', marketViability: 'VIABLE',
  },
  { iso2: 'RW', iso3: 'RWA', numeric: '646', name: 'Rwanda', tier: 'not-analyzed' },
  { iso2: 'ST', iso3: 'STP', numeric: '678', name: 'São Tomé and Príncipe', tier: 'not-analyzed' },
  { iso2: 'SN', iso3: 'SEN', numeric: '686', name: 'Senegal', tier: 'not-analyzed' },
  { iso2: 'SC', iso3: 'SYC', numeric: '690', name: 'Seychelles', tier: 'not-analyzed' },
  { iso2: 'SL', iso3: 'SLE', numeric: '694', name: 'Sierra Leone', tier: 'not-analyzed' },
  { iso2: 'SO', iso3: 'SOM', numeric: '706', name: 'Somalia', tier: 'not-analyzed' },
  {
    iso2: 'ZA', iso3: 'ZAF', numeric: '710', name: 'South Africa',
    tier: 'moderate',
    summary: 'Resale legal, no price caps. Requires local SA entity, TPPP registration, Paystack (Stripe N/A), POPIA compliance. Cross-border US blocked by exchange controls.',
    lastUpdated: '2026-04-15', analysisKey: 'SouthAfrica', marketViability: 'VIABLE',
  },
  { iso2: 'SS', iso3: 'SSD', numeric: '728', name: 'South Sudan', tier: 'not-analyzed' },
  { iso2: 'SD', iso3: 'SDN', numeric: '729', name: 'Sudan', tier: 'not-analyzed' },
  { iso2: 'TZ', iso3: 'TZA', numeric: '834', name: 'Tanzania', tier: 'not-analyzed' },
  { iso2: 'TG', iso3: 'TGO', numeric: '768', name: 'Togo', tier: 'not-analyzed' },
  { iso2: 'TN', iso3: 'TUN', numeric: '788', name: 'Tunisia', tier: 'not-analyzed' },
  { iso2: 'UG', iso3: 'UGA', numeric: '800', name: 'Uganda', tier: 'not-analyzed' },
  { iso2: 'ZM', iso3: 'ZMB', numeric: '894', name: 'Zambia', tier: 'not-analyzed' },
  { iso2: 'ZW', iso3: 'ZWE', numeric: '716', name: 'Zimbabwe', tier: 'not-analyzed' },

  // ── Asia ──────────────────────────────────────────────────────────
  { iso2: 'AF', iso3: 'AFG', numeric: '004', name: 'Afghanistan', tier: 'not-analyzed' },
  { iso2: 'AM', iso3: 'ARM', numeric: '051', name: 'Armenia', tier: 'not-analyzed' },
  { iso2: 'AZ', iso3: 'AZE', numeric: '031', name: 'Azerbaijan', tier: 'not-analyzed' },
  { iso2: 'BH', iso3: 'BHR', numeric: '048', name: 'Bahrain', tier: 'not-analyzed' },
  { iso2: 'BD', iso3: 'BGD', numeric: '050', name: 'Bangladesh', tier: 'not-analyzed' },
  { iso2: 'BT', iso3: 'BTN', numeric: '064', name: 'Bhutan', tier: 'not-analyzed' },
  { iso2: 'BN', iso3: 'BRN', numeric: '096', name: 'Brunei', tier: 'not-analyzed' },
  { iso2: 'KH', iso3: 'KHM', numeric: '116', name: 'Cambodia', tier: 'not-analyzed' },
  { iso2: 'CN', iso3: 'CHN', numeric: '156', name: 'China', tier: 'not-analyzed' },
  { iso2: 'GE', iso3: 'GEO', numeric: '268', name: 'Georgia', tier: 'not-analyzed' },
  {
    iso2: 'HK', iso3: 'HKG', numeric: '344', name: 'Hong Kong',
    tier: 'easy',
    summary: 'One of the most permissive jurisdictions. No VAT/GST, no entertainment tax, no mandatory e-invoicing, no effective anti-scalping. Stripe Connect handles fund custody.',
    lastUpdated: '2026-04-15', analysisKey: 'HongKong', marketViability: 'VIABLE',
  },
  {
    iso2: 'IN', iso3: 'IND', numeric: '356', name: 'India',
    tier: 'moderate',
    summary: 'Local Indian ticketing co viable (Menta US cross-border BLOCKED). BNS §112 criminalizes unauthorized selling — organizer auth is key. GST 18% on fees. 20% WHT on Menta fees.',
    lastUpdated: '2026-04-16', analysisKey: 'India', marketViability: 'VIABLE',
  },
  {
    iso2: 'ID', iso3: 'IDN', numeric: '360', name: 'Indonesia',
    tier: 'moderate',
    summary: 'Resale legal. Local entity required, Stripe Connect preview-only. 11% PPN on fees (ticket exempt). BI PJP license risk (mitigated by Stripe). 10% PPh 26 on Menta SaaS.',
    lastUpdated: '2026-04-15', analysisKey: 'Indonesia', marketViability: 'VIABLE',
  },
  { iso2: 'IR', iso3: 'IRN', numeric: '364', name: 'Iran', tier: 'not-analyzed' },
  { iso2: 'IQ', iso3: 'IRQ', numeric: '368', name: 'Iraq', tier: 'not-analyzed' },
  {
    iso2: 'IL', iso3: 'ISR', numeric: '376', name: 'Israel',
    tier: 'restricted',
    summary: 'Penal Code §194a criminalizes above-face-value resale by unlicensed. Local ticketing co required. 20-30% WHT on payouts. 14-day cooling-off applies (unlike EU). Stripe N/A.',
    lastUpdated: '2026-04-16', analysisKey: 'Israel', marketViability: 'GRAY',
  },
  {
    iso2: 'JP', iso3: 'JPN', numeric: '392', name: 'Japan',
    tier: 'easy',
    summary: 'Tier 1 market. 2018 Ticket Resale Act designed for Menta\'s organizer-authorized face-value model. 0% WHT on Menta fees (US treaty). Stripe Connect works. Only constraint: face-value cap on ticket.',
    lastUpdated: '2026-04-15', analysisKey: 'Japan', marketViability: 'VIABLE',
  },
  { iso2: 'JO', iso3: 'JOR', numeric: '400', name: 'Jordan', tier: 'not-analyzed' },
  { iso2: 'KZ', iso3: 'KAZ', numeric: '398', name: 'Kazakhstan', tier: 'not-analyzed' },
  { iso2: 'KW', iso3: 'KWT', numeric: '414', name: 'Kuwait', tier: 'not-analyzed' },
  { iso2: 'KG', iso3: 'KGZ', numeric: '417', name: 'Kyrgyzstan', tier: 'not-analyzed' },
  { iso2: 'LA', iso3: 'LAO', numeric: '418', name: 'Laos', tier: 'not-analyzed' },
  { iso2: 'LB', iso3: 'LBN', numeric: '422', name: 'Lebanon', tier: 'not-analyzed' },
  { iso2: 'MO', iso3: 'MAC', numeric: '446', name: 'Macao', tier: 'not-analyzed' },
  {
    iso2: 'MY', iso3: 'MYS', numeric: '458', name: 'Malaysia',
    tier: 'moderate',
    summary: 'Viable today. Resale legal, 8% SST on fees, Stripe Connect works. Anti-scalping legislation actively under development (inter-ministerial review). 10% WHT on Menta SaaS (no US treaty).',
    lastUpdated: '2026-04-15', analysisKey: 'Malaysia', marketViability: 'VIABLE',
  },
  { iso2: 'MV', iso3: 'MDV', numeric: '462', name: 'Maldives', tier: 'not-analyzed' },
  { iso2: 'MN', iso3: 'MNG', numeric: '496', name: 'Mongolia', tier: 'not-analyzed' },
  { iso2: 'MM', iso3: 'MMR', numeric: '104', name: 'Myanmar', tier: 'not-analyzed' },
  { iso2: 'NP', iso3: 'NPL', numeric: '524', name: 'Nepal', tier: 'not-analyzed' },
  { iso2: 'KP', iso3: 'PRK', numeric: '408', name: 'North Korea', tier: 'not-analyzed' },
  { iso2: 'OM', iso3: 'OMN', numeric: '512', name: 'Oman', tier: 'not-analyzed' },
  { iso2: 'PK', iso3: 'PAK', numeric: '586', name: 'Pakistan', tier: 'not-analyzed' },
  { iso2: 'PS', iso3: 'PSE', numeric: '275', name: 'Palestine', tier: 'not-analyzed' },
  { iso2: 'PH', iso3: 'PHL', numeric: '608', name: 'Philippines', tier: 'not-analyzed' },
  {
    iso2: 'QA', iso3: 'QAT', numeric: '634', name: 'Qatar',
    tier: 'moderate',
    summary: 'Clean tax environment (no VAT, no PIT). Structural constraints: QCB payment licensing, 2026 e-commerce license mandate, 5% cross-border WHT (no US treaty), Stripe unavailable.',
    lastUpdated: '2026-04-15', analysisKey: 'Qatar', marketViability: 'VIABLE',
  },
  {
    iso2: 'SA', iso3: 'SAU', numeric: '682', name: 'Saudi Arabia',
    tier: 'moderate',
    summary: 'Resale legal — gov\'t-endorsed platforms operate. Local partner + SAMA PSP (Tap/Moyasar, Stripe N/A), FATOORA e-invoicing, PDPL. 15% VAT + deemed supplier (2026). 15% WHT to Menta.',
    lastUpdated: '2026-04-15', analysisKey: 'SaudiArabia', marketViability: 'VIABLE',
  },
  {
    iso2: 'SG', iso3: 'SGP', numeric: '702', name: 'Singapore',
    tier: 'moderate',
    summary: 'Resale explicitly legal (MTI confirmed). No price caps. 9% GST on fees. PS Act payment licensing risk (MEDIUM). 10% royalty WHT to US (no treaty) — structure B2B carefully.',
    lastUpdated: '2026-04-16', analysisKey: 'Singapore', marketViability: 'VIABLE',
  },
  {
    iso2: 'KR', iso3: 'KOR', numeric: '410', name: 'South Korea',
    tier: 'restricted',
    summary: 'Jan 2026 Scalping Eradication Law bans above-face-value habitual resale (up to 50× sale amount, 1yr prison). Menta face-value model legal. Stripe Connect cannot pay out to Korean sellers.',
    lastUpdated: '2026-04-15', analysisKey: 'SouthKorea', marketViability: 'GRAY',
  },
  { iso2: 'LK', iso3: 'LKA', numeric: '144', name: 'Sri Lanka', tier: 'not-analyzed' },
  { iso2: 'SY', iso3: 'SYR', numeric: '760', name: 'Syria', tier: 'not-analyzed' },
  { iso2: 'TW', iso3: 'TWN', numeric: '158', name: 'Taiwan', tier: 'not-analyzed' },
  { iso2: 'TJ', iso3: 'TJK', numeric: '762', name: 'Tajikistan', tier: 'not-analyzed' },
  {
    iso2: 'TH', iso3: 'THA', numeric: '764', name: 'Thailand',
    tier: 'moderate',
    summary: 'Resale legal. 7% VAT on fees, 5% treaty WHT on Menta. ETDA platform registration mandatory. Stripe Connect 10-day manual payout may conflict with post-event hold.',
    lastUpdated: '2026-04-15', analysisKey: 'Thailand', marketViability: 'VIABLE',
  },
  { iso2: 'TL', iso3: 'TLS', numeric: '626', name: 'Timor-Leste', tier: 'not-analyzed' },
  { iso2: 'TM', iso3: 'TKM', numeric: '795', name: 'Turkmenistan', tier: 'not-analyzed' },
  {
    iso2: 'AE', iso3: 'ARE', numeric: '784', name: 'United Arab Emirates',
    tier: 'moderate',
    summary: 'Favorable tax: 0% WHT cross-border, 5% VAT on fees, no PIT. Resale legal. Stripe Connect requires UAE entity; new CBUAE Law (criminal up to AED 1B). Arabic language mandatory.',
    lastUpdated: '2026-04-15', analysisKey: 'UAE', marketViability: 'VIABLE',
  },
  { iso2: 'UZ', iso3: 'UZB', numeric: '860', name: 'Uzbekistan', tier: 'not-analyzed' },
  { iso2: 'VN', iso3: 'VNM', numeric: '704', name: 'Vietnam', tier: 'not-analyzed' },
  { iso2: 'YE', iso3: 'YEM', numeric: '887', name: 'Yemen', tier: 'not-analyzed' },

  // ── Oceania ───────────────────────────────────────────────────────
  {
    iso2: 'AU', iso3: 'AUS', numeric: '036', name: 'Australia',
    tier: 'moderate',
    summary: 'Menta US operates cross-border — no local entity. Simplified GST at A$75K. State scalping patchwork (VIC exemption, NSW/SA/WA 110% caps, QLD 110% no exemption). ACCC drip-pricing #1 risk.',
    lastUpdated: '2026-04-16', analysisKey: 'Australia', marketViability: 'VIABLE',
  },
  { iso2: 'FJ', iso3: 'FJI', numeric: '242', name: 'Fiji', tier: 'not-analyzed' },
  { iso2: 'KI', iso3: 'KIR', numeric: '296', name: 'Kiribati', tier: 'not-analyzed' },
  { iso2: 'MH', iso3: 'MHL', numeric: '584', name: 'Marshall Islands', tier: 'not-analyzed' },
  { iso2: 'FM', iso3: 'FSM', numeric: '583', name: 'Micronesia', tier: 'not-analyzed' },
  { iso2: 'NR', iso3: 'NRU', numeric: '520', name: 'Nauru', tier: 'not-analyzed' },
  {
    iso2: 'NZ', iso3: 'NZL', numeric: '554', name: 'New Zealand',
    tier: 'easy',
    summary: 'Menta US operates direct cross-border via Stripe Connect — no local entity. Resale legal (MEMA exception for rare major events). GST 15% on fees via Remote Services. No withholding.',
    lastUpdated: '2026-04-16', analysisKey: 'NewZealand', marketViability: 'VIABLE',
  },
  { iso2: 'PW', iso3: 'PLW', numeric: '585', name: 'Palau', tier: 'not-analyzed' },
  { iso2: 'PG', iso3: 'PNG', numeric: '598', name: 'Papua New Guinea', tier: 'not-analyzed' },
  { iso2: 'WS', iso3: 'WSM', numeric: '882', name: 'Samoa', tier: 'not-analyzed' },
  { iso2: 'SB', iso3: 'SLB', numeric: '090', name: 'Solomon Islands', tier: 'not-analyzed' },
  { iso2: 'TO', iso3: 'TON', numeric: '776', name: 'Tonga', tier: 'not-analyzed' },
  { iso2: 'TV', iso3: 'TUV', numeric: '798', name: 'Tuvalu', tier: 'not-analyzed' },
  { iso2: 'VU', iso3: 'VUT', numeric: '548', name: 'Vanuatu', tier: 'not-analyzed' },
];

export const COUNTRY_BY_ISO2 = Object.fromEntries(
  COUNTRIES.map((c) => [c.iso2, c]),
) as Record<string, CountryMeta>;

export const COUNTRY_BY_NUMERIC = Object.fromEntries(
  COUNTRIES.map((c) => [c.numeric, c]),
) as Record<string, CountryMeta>;

export const COUNTRY_BY_NAME = Object.fromEntries(
  COUNTRIES.map((c) => [c.name.toLowerCase(), c]),
) as Record<string, CountryMeta>;
